import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchESScans, exportESScans, fetchAggregatedESScans } from './thunks';
import { DateTime } from 'luxon';
import {
	ScanLogAggregations,
	ScanLogResponse,
	ScanLogsFilter,
	ScanlogsState,
} from 'src/types/scanLogTypes';

const initialState: ScanlogsState = {
	tableData: null,
	loading: false,
	error: null,
	page: 1,
	pageSize: 25,
	totalCount: 0,
	count: 0,
	searchText: '',
	searchField: '',
	sort: 'scanDate',
	sortDir: 'desc',
	dateRangeStart: DateTime.local()
		.minus({ days: 30 })
		.startOf('day')
		.toUTC()
		.toISO(),
	dateRangeEnd: DateTime.local().endOf('day').toUTC().toISO(),
	filters: [],
	filtersAggregation: null,
	toast: {
		message: '',
		type: 'success',
	},
};

const scanlogsSlice = createSlice({
	name: 'scanlogs',
	initialState,
	reducers: {
		resetScanlogsState: () => initialState,
		updatePage: (state, action: PayloadAction<number>) => {
			state.page = action.payload;
		},
		updatePageSize: (state, action: PayloadAction<number>) => {
			state.pageSize = action.payload;
		},
		updateDateRange: (
			state,
			action: PayloadAction<{
				start: string | null;
				end: string | null;
			}>,
		) => {
			state.dateRangeStart = action.payload.start;
			state.dateRangeEnd = action.payload.end;
		},
		updateSearchTextAndField: (
			state,
			action: PayloadAction<{ text: string; field: string }>,
		) => {
			state.searchText = action.payload.text;
			state.searchField = action.payload.field;
		},
		updateSort: (
			state,
			action: PayloadAction<{ sort: string; sortDir: string }>,
		) => {
			state.sort = action.payload.sort;
			state.sortDir = action.payload.sortDir;
		},
		pushToast: (
			state,
			action: PayloadAction<{
				message: string;
				type: 'info' | 'success' | 'error' | 'warning' | 'default';
			}>,
		) => {
			state.toast = { ...state.toast, ...action.payload };
		},
		updateFilters: (state, action: PayloadAction<ScanLogsFilter[]>) => {
			state.filters = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchESScans.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				fetchESScans.fulfilled,
				(state, action: PayloadAction<ScanLogResponse>) => {
					state.loading = false;
					state.tableData = action.payload.data;
					state.error = null;
					state.totalCount = action.payload.totalCount;
					state.count = action.payload.count;
				},
			)
			.addCase(fetchESScans.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(exportESScans.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(exportESScans.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(exportESScans.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(fetchAggregatedESScans.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				fetchAggregatedESScans.fulfilled,
				(state, action: PayloadAction<ScanLogAggregations>) => {
					state.loading = false;
					state.filtersAggregation = action.payload;
					state.error = null;
				},
			)
			.addCase(fetchAggregatedESScans.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			});
	},
});

export const {
	resetScanlogsState,
	updatePageSize,
	updatePage,
	updateDateRange,
	updateSearchTextAndField,
	updateSort,
	pushToast,
	updateFilters,
} = scanlogsSlice.actions;
export default scanlogsSlice.reducer;
